import React, { FormEvent, ChangeEvent } from "react";
import { Box, Button, TextField, CircularProgress, Typography } from "@mui/material";
import { useAppContext } from "../../../../context/AppContext";
import { COMMON_MUI_STYLES } from "../../../../styles";
import { PasswordField } from "./PasswordField";

interface LoginFormProps {
  email: string;
  password: string;
  showPassword: boolean;
  loginErrors: { [key: string]: string };
  isLoading: boolean;
  onEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onTogglePassword: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onForgotPasswordClick: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  email,
  password,
  showPassword,
  loginErrors,
  isLoading,
  onEmailChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
  onForgotPasswordClick,
}) => {
  const {
    getTranslation,
    distributorId,
    hideDistributorSignupCta,
    setCurrentView,
  } = useAppContext();

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      noValidate
      sx={{
        display: "flex",
        flexDirection: "column",
        width: { xs: "90%", sm: "35%" },
        justifyContent: { xs: "center", sm: "start" },   // center vertically on mobile
        mt: { xs: 0, sm: 10 },                           // no extra top margin on mobile
        minHeight: { xs: "calc(100vh - 160px)", sm: "auto" }, // take most of screen height on mobile
        alignItems: "center",
        gap: { xs: 2, sm: 3 },
      }}
    >
      <Typography sx={{ ...COMMON_MUI_STYLES.title1, mb: 3 }}>
          {getTranslation("Txt6")}
        </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%" ,
        }}
      >
        <TextField
          margin="normal"
          fullWidth
          id="email"
          placeholder={getTranslation("Txt7").replace(" *", "")}
          name="email"
          autoComplete="email"
          value={email}
          onChange={onEmailChange}
          error={!!loginErrors.email}
          helperText={loginErrors.email}
          sx={COMMON_MUI_STYLES.textField}
        />
        <PasswordField
          name="password"
          autoComplete="current-password"
          placeholder={getTranslation("Txt8")}
          value={password || ""}
          onChange={onPasswordChange}
          showPassword={showPassword}
          onTogglePassword={onTogglePassword}
          error={!!loginErrors.password}
          helperText={loginErrors.password}
        />
        <Box sx={{ width: "100%", textAlign: "right", mt: 1 }}>
          <Button
            onClick={onForgotPasswordClick}
            variant="text"
            size="medium"
            sx={{
              textTransform: "none",
              color: "var(--color-text-primary)",
              fontWeight: 500,
              p: 0,
              minWidth: 0,
              textDecoration: "underline",
              "&:hover": {
                textDecoration: "underline",
                backgroundColor: "transparent",
              },
            }}
          >
            {getTranslation("Txt9")}
          </Button>
        </Box>
      </Box>
      <Button
        type="submit"
        variant="contained"
        disabled={isLoading}
        sx={{
          ...COMMON_MUI_STYLES.primaryCtaButton1,
          '& > *:not(.loader-overlay)': {
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.2s ease-in-out',
          },
        }}
      >
        <span>{getTranslation("Txt10")}</span>
        {isLoading && (
          <CircularProgress 
            size={20} 
            sx={{ 
              color: "var(--color-text-inverse)", 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              marginTop: '-10px',
              marginLeft: '-10px',
            }} 
            className="loader-overlay"
          />
        )}
      </Button>
          <Typography
            variant="body1"
            sx={{ color: "var(--color-text-primary)", fontWeight: 700, textAlign: "center", mt: 2 }}
          >
            {distributorId && !hideDistributorSignupCta ? (
              <>
                <Button
                  variant="text"
                  size="medium"
                  onClick={() => setCurrentView("signup")}
                  sx={{
                    textTransform: "none",
                    color: "var(--color-text-primary)",
                    fontWeight: 700,
                    textDecoration: "underline",
                    p: 0,
                    minWidth: 0,
                    fontSize: "1.1rem",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {getTranslation("Txt19")}
                </Button>
              </>
            ) : (
              getTranslation("Txt828")
            )}
          </Typography>
 
    </Box>
  );
};
